import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import orderBy from 'lodash/orderBy';

import type { WorkloadCostReportItem } from '@cast/types';
import { toBig } from '@cast/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

import type { ComputeSpendDatapoint } from '../_types';
import type { ComputeGranularity } from './getComputeGranularity';

export const mapWorkloadsToDatapoints = (
  workloads: WorkloadCostReportItem[],
  unit: ComputeGranularity
): ComputeSpendDatapoint[] => {
  const costsByTimestamp: Record<string, Record<string, number>> = {};

  for (const { workloadName, costMetrics } of workloads) {
    if (!costMetrics || !workloadName) {
      continue;
    }

    for (const {
      timestamp,
      costOnDemand,
      costSpot,
      costSpotFallback,
      uptimeMinutesOnDemand,
      uptimeMinutesSpot,
      uptimeMinutesSpotFallback,
    } of costMetrics) {
      if (!timestamp) {
        continue;
      }

      const key = dayjs.utc(timestamp).subtract(1, unit).toISOString();
      const values = costsByTimestamp[key] || {};

      const totalOnDemand = toBig(uptimeMinutesOnDemand || 0)
        .div(60)
        .mul(costOnDemand || 0);
      const totalSpot = toBig(uptimeMinutesSpot || 0)
        .div(60)
        .mul(costSpot || 0);
      const totalSpotFallback = toBig(uptimeMinutesSpotFallback || 0)
        .div(60)
        .mul(costSpotFallback || 0);

      values[workloadName] = totalOnDemand
        .add(totalSpot)
        .add(totalSpotFallback)
        .toNumber();

      costsByTimestamp[key] = values;
    }
  }

  return orderBy(
    Object.entries(costsByTimestamp).map(([timestamp, values]) => ({
      timestamp,
      chartTimestampUnix: new Date(timestamp).getTime() / 1000,
      values,
    })),
    'timestamp'
  );
};
